// apps/flux-ev-web/src/components/ev/ScenarioEditor.jsx
/**
 * ScenarioEditor - Edit a single scenario with Fleet/Chargers/Utility/Schedule/Results/Financial tabs
 *
 * Tabs are driven by the sidebar (RetroContext evScenarioTab) or activeTab prop.
 * Tab indices: 0=Fleet, 1=Chargers, 2=Utility, 3=Schedule, 4=Results, 5=Financial
 */

import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Paper,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    InputAdornment,
    Chip,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    alpha,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    PlayArrow as SimulateIcon,
    Assessment as ResultsIcon,
    Save as SaveIcon,
    Speed as DemandIcon,
    LocalShipping as FleetIcon,
    BoltOutlined as BoltIcon,
    EvStation as ChargerIcon,
    LocationOn as LocationIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useThemeMode, vortxColors, vortxTypography, SectionCard, useResponsive } from '@vortx/shared-ui';
import { SaveStatusIndicator } from '../SaveStatusIndicator';
import ChargerConfig from './ChargerConfig';
import { useChargerDatabase } from '../../features/ev/hooks/useChargerDatabase';
import FleetSection from '../../features/ev/sections/FleetSection';
import UtilitySection from '../../features/ev/sections/UtilitySection';
import FinancialSection from '../../features/ev/sections/FinancialSection';
import EVLoadChart from './EVLoadChart';
import SOCTrajectoryChart from './SOCTrajectoryChart';
import ChargingScheduleChart from './ChargingScheduleChart';


const STRATEGY_LABELS = {
    opportunity: 'Opportunity',
    demand_limit: 'Demand Limited',
    cost_optimized: 'Economic',
};

/**
 * ResultsPreview - Compact results summary for the editor
 */
function ResultsPreview({ results, strategyKey }) {
    // Use selected strategy, fall back to opportunity, then legacy flat shape
    const metrics = (strategyKey ? results?.strategies?.[strategyKey]?.metrics : null)
        || results?.strategies?.opportunity?.metrics
        || results?.metrics || {};

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                bgcolor: alpha(vortxColors.teal, 0.1),
                border: `1px solid ${alpha(vortxColors.teal, 0.3)}`,
                borderRadius: 1,
            }}
        >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Last Simulation Results
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Peak Demand
                    </Typography>
                    <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 600 }}>
                        {Math.round(metrics.peak_demand_kw || 0).toLocaleString()} kW
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Daily Energy
                    </Typography>
                    <Typography variant="h6" sx={{ color: vortxColors.gold, fontWeight: 600 }}>
                        {Math.round(metrics.daily_energy_kwh || 0).toLocaleString()} kWh
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Avg Utilization
                    </Typography>
                    <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 600 }}>
                        {Math.round((metrics.avg_utilization || 0) * 100)}%
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Completion Rate
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: (metrics.completion_rate || 1) < 1 ? '#ff6b6b' : vortxColors.teal,
                            fontWeight: 600,
                        }}
                    >
                        {Math.round((metrics.completion_rate || 1) * 100)}%
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
}

/**
 * CopyFromSelect - Dropdown to copy fleet/chargers from another scenario
 */
function CopyFromSelect({ label, scenarios, currentScenarioId, onCopy }) {
    const otherScenarios = useMemo(
        () => (scenarios || []).filter(s => s.id !== currentScenarioId),
        [scenarios, currentScenarioId]
    );

    if (otherScenarios.length === 0) return null;

    return (
        <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value=""
                label={label}
                onChange={(e) => {
                    const source = scenarios.find(s => s.id === e.target.value);
                    if (source) onCopy(source);
                }}
                startAdornment={<CopyIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} />}
            >
                {otherScenarios.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name || 'Unnamed'}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

/**
 * SiteSummaryBar - Read-only project site info at top of scenario editor
 */
function SiteSummaryBar({ projectData }) {
    if (!projectData) return null;

    const location = projectData.location || {};
    const locationStr = [location.city, location.state].filter(Boolean).join(', ') || location.address || null;
    const voltage = projectData.service_voltage;
    const capacity = projectData.site_config?.available_capacity_kw;
    const tariff = projectData.tariff_name;

    // Don't render if no site info at all
    if (!locationStr && !voltage && !capacity && !tariff) return null;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: alpha(vortxColors.teal, 0.05),
            border: `1px solid ${alpha(vortxColors.teal, 0.15)}`,
            flexWrap: 'wrap',
        }}>
            <LocationIcon sx={{ fontSize: 18, color: vortxColors.teal, opacity: 0.7 }} />
            {locationStr && (
                <Chip label={locationStr} size="small" variant="outlined"
                    sx={{ borderColor: alpha(vortxColors.teal, 0.3), color: 'text.secondary', fontWeight: 500 }} />
            )}
            {voltage && (
                <Chip label={`${voltage}V`} size="small" variant="outlined"
                    sx={{ borderColor: alpha(vortxColors.teal, 0.3), color: 'text.secondary', fontWeight: 500 }} />
            )}
            {capacity && (
                <Chip label={`${capacity} kW capacity`} size="small" variant="outlined"
                    sx={{ borderColor: alpha(vortxColors.teal, 0.3), color: 'text.secondary', fontWeight: 500 }} />
            )}
            {tariff && (
                <Chip label={tariff} size="small" variant="outlined"
                    sx={{ borderColor: alpha(vortxColors.teal, 0.3), color: 'text.secondary', fontWeight: 500 }} />
            )}
        </Box>
    );
}

/**
 * DepotScheduleTimeline - Visual timeline of vehicle depot availability
 *
 * Shows a Gantt-style chart: X-axis = hours (0-24), Y-axis = vehicle groups.
 * Green bars indicate "at depot" windows (return_time → departure_time).
 * Weekly schedule vehicles show 7 rows per group (Mon-Sun).
 */
function DepotScheduleTimeline({ fleet }) {
    const { isDark } = useThemeMode();

    const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const HOURS = Array.from({ length: 25 }, (_, i) => i);

    // Parse HH:MM time to fractional hours
    const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const [h, m] = timeStr.split(':').map(Number);
        return h + (m || 0) / 60;
    };

    // Build schedule rows from fleet
    const rows = useMemo(() => {
        const result = [];
        (fleet || []).forEach((vehicle) => {
            const name = vehicle.name || vehicle.vehicle_type || 'Vehicle';
            const qty = vehicle.quantity || 1;
            const label = qty > 1 ? `${name} (x${qty})` : name;

            if (vehicle.schedule_mode === 'weekly' && vehicle.weekly_schedule) {
                // Weekly schedule: one row per day
                DAYS.forEach((day) => {
                    const dayData = vehicle.weekly_schedule[day.toLowerCase()];
                    if (dayData === null || dayData === undefined) {
                        // Off day — vehicle at depot all day
                        result.push({ label: `${label} — ${day}`, segments: [{ start: 0, end: 24 }], qty });
                    } else {
                        const dep = parseTime(dayData.departure_time) ?? 6;
                        const ret = parseTime(dayData.return_time) ?? 18;
                        // At depot: 0→departure, return→24
                        const segments = [];
                        if (dep > 0) segments.push({ start: 0, end: dep });
                        if (ret < 24) segments.push({ start: ret, end: 24 });
                        result.push({ label: `${label} — ${day}`, segments, qty });
                    }
                });
            } else {
                // Simple daily schedule
                const dep = parseTime(vehicle.departure_time) ?? 6;
                const ret = parseTime(vehicle.return_time) ?? 18;
                const segments = [];
                if (dep > 0) segments.push({ start: 0, end: dep });
                if (ret < 24) segments.push({ start: ret, end: 24 });
                result.push({ label, segments, qty });
            }
        });
        return result;
    }, [fleet]);

    if (rows.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">
                    Add vehicles in the Fleet tab to see the depot schedule.
                </Typography>
            </Box>
        );
    }

    const ROW_HEIGHT = 32;
    const LABEL_WIDTH = 180;
    const CHART_WIDTH_PX = 600; // chart area width in px

    return (
        <SectionCard title="Depot Availability Schedule" icon={<FleetIcon />}>
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: LABEL_WIDTH + CHART_WIDTH_PX + 20 }}>
                    {/* Hour labels */}
                    <Box sx={{ position: 'relative', height: 16, ml: `${LABEL_WIDTH}px`, mb: 0.5 }}>
                        {HOURS.filter(h => h % 3 === 0).map(h => (
                            <Typography
                                key={h}
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    left: `${LABEL_WIDTH + (h / 24) * CHART_WIDTH_PX}px`,
                                    color: 'text.secondary',
                                    fontSize: '0.65rem',
                                    transform: 'translateX(-50%)',
                                }}
                            >
                                {h === 0 ? '12A' : h === 12 ? '12P' : h < 12 ? `${h}A` : `${h - 12}P`}
                            </Typography>
                        ))}
                    </Box>

                    {/* Rows */}
                    <Box sx={{ position: 'relative', mt: 2.5 }}>
                        {rows.map((row, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: ROW_HEIGHT,
                                    mb: 0.25,
                                }}
                            >
                                {/* Label */}
                                <Tooltip title={`${row.qty} vehicle${row.qty > 1 ? 's' : ''}`} placement="left" arrow>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            width: LABEL_WIDTH,
                                            flexShrink: 0,
                                            pr: 1,
                                            textAlign: 'right',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                </Tooltip>

                                {/* Timeline bar */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: CHART_WIDTH_PX,
                                        height: ROW_HEIGHT - 4,
                                        bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                        borderRadius: 0.5,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {row.segments.map((seg, si) => (
                                        <Box
                                            key={si}
                                            sx={{
                                                position: 'absolute',
                                                left: `${(seg.start / 24) * 100}%`,
                                                width: `${((seg.end - seg.start) / 24) * 100}%`,
                                                top: 2,
                                                bottom: 2,
                                                bgcolor: alpha('#4CAF50', 0.5),
                                                borderRadius: 0.5,
                                                border: `1px solid ${alpha('#4CAF50', 0.7)}`,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}

                        {/* Vertical hour gridlines */}
                        {HOURS.filter(h => h % 3 === 0 && h > 0 && h < 24).map(h => (
                            <Box
                                key={h}
                                sx={{
                                    position: 'absolute',
                                    left: `${LABEL_WIDTH + (h / 24) * CHART_WIDTH_PX}px`,
                                    top: 0,
                                    bottom: 0,
                                    width: '1px',
                                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                                    pointerEvents: 'none',
                                }}
                            />
                        ))}
                    </Box>

                    {/* Legend */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, ml: `${LABEL_WIDTH}px` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 14, height: 14, bgcolor: alpha('#4CAF50', 0.5), border: `1px solid ${alpha('#4CAF50', 0.7)}`, borderRadius: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">At depot (charging available)</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 14, height: 14, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">Away from depot</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </SectionCard>
    );
}

/**
 * ScenarioEditor Component
 *
 * @param {Object} props
 * @param {Object} props.scenario - Scenario to edit (includes fleet, chargers, results)
 * @param {number} props.siteCapacity - Site capacity kW for charger warnings
 * @param {Array} props.allScenarios - All scenarios in this project (for "copy from" dropdowns)
 * @param {Object} props.projectData - Full project data (for site summary bar)
 * @param {Function} props.onChange - Called with partial updates
 * @param {Function} props.onSave - Save scenario changes
 * @param {string} props.saveStatus - Save status (idle, dirty, saving, saved, error)
 * @param {string} props.saveError - Save error message
 * @param {string} props.lastSavedAt - ISO timestamp of last save
 * @param {Function} props.onBack - Go back to list
 * @param {Function} props.onSimulate - Run simulation
 * @param {boolean} props.simulating - Whether simulation is running
 * @param {number} props.activeTab - Active tab index (0=Fleet, 1=Chargers, 2=Utility, 3=Schedule, 4=Results, 5=Financial) driven by sidebar
 */
function ScenarioEditor({
    scenario,
    siteCapacity,
    allScenarios = [],
    projectData = null,
    onChange,
    onSave,
    saveStatus = 'idle',
    saveError = null,
    lastSavedAt = null,
    onBack,
    onSimulate,
    simulating = false,
    socTrajectories = null,
    activeTab: activeTabProp,
}) {
    const { isDark } = useThemeMode();
    const { isMobile } = useResponsive();
    const { chargers: chargerDatabase } = useChargerDatabase();
    // Use prop-driven tab (from sidebar/RetroContext), default to 0 (Fleet)
    const activeTab = activeTabProp ?? 0;
    const [resultsTab, setResultsTab] = useState('load');
    const [viewStrategy, setViewStrategy] = useState(null);

    // Derive which backend strategy key to display in Results tab
    const activeStrategyKey = useMemo(() => {
        const strategies = scenario?.results?.strategies;
        if (!strategies) return null;
        // If user picked via Results tab selector, use it
        if (viewStrategy && strategies[viewStrategy]?.metrics) return viewStrategy;
        // Default: map from scenario's optimization_mode setting
        const modeMap = { opportunity: 'opportunity', demand_limited: 'demand_limit', optimized: 'cost_optimized' };
        const mapped = modeMap[scenario?.optimization_mode];
        if (mapped && strategies[mapped]?.metrics) return mapped;
        // Fallback: first available with metrics
        for (const key of ['cost_optimized', 'demand_limit', 'opportunity']) {
            if (strategies[key]?.metrics) return key;
        }
        return null;
    }, [scenario?.results?.strategies, scenario?.optimization_mode, viewStrategy]);

    // Handler functions
    const handleNameChange = (e) => onChange({ name: e.target.value });
    const handleChargersChange = (chargers) => onChange({ chargers });
    const handleFleetChange = (fleet) => onChange({ fleet });
    const handleDemandLimitChange = (e) => {
        const val = e.target.value === '' ? null : Number(e.target.value);
        onChange({ max_demand_kw: val });
    };

    // Copy fleet from another scenario
    const handleCopyFleet = (sourceScenario) => {
        if (sourceScenario.fleet) {
            onChange({ fleet: JSON.parse(JSON.stringify(sourceScenario.fleet)) });
        }
    };

    // Copy chargers from another scenario
    const handleCopyChargers = (sourceScenario) => {
        const updates = {};
        if (sourceScenario.chargers) {
            updates.chargers = JSON.parse(JSON.stringify(sourceScenario.chargers));
        }
        if (sourceScenario.max_demand_kw !== undefined) {
            updates.max_demand_kw = sourceScenario.max_demand_kw;
        }
        onChange(updates);
    };

    if (!scenario) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No scenario selected</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                    <IconButton
                        onClick={onBack}
                        sx={{
                            color: vortxColors.gold,
                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) },
                        }}
                    >
                        <BackIcon />
                    </IconButton>
                    <TextField
                        value={scenario.name || ''}
                        onChange={handleNameChange}
                        variant="standard"
                        placeholder="Scenario Name"
                        sx={{
                            flex: 1,
                            '& .MuiInput-root': {
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.heading,
                            },
                            '& .MuiInput-underline:before': {
                                borderBottomColor: alpha(vortxColors.gold, 0.3),
                            },
                            '& .MuiInput-underline:hover:before': {
                                borderBottomColor: vortxColors.gold,
                            },
                            '& .MuiInput-underline:after': {
                                borderBottomColor: vortxColors.gold,
                            },
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                    <SaveStatusIndicator
                        status={saveStatus}
                        error={saveError}
                        lastSavedAt={lastSavedAt}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={onSave}
                        disabled={saveStatus === 'saving'}
                        sx={{
                            borderColor: vortxColors.gold,
                            color: vortxColors.gold,
                            flex: isMobile ? 1 : 'none',
                        }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={simulating ? <CircularProgress size={18} color="inherit" /> : <SimulateIcon />}
                        onClick={onSimulate}
                        disabled={simulating}
                        sx={{
                            bgcolor: vortxColors.teal,
                            '&:hover': { bgcolor: vortxColors.darkGreen },
                            flex: isMobile ? 1 : 'none',
                        }}
                    >
                        {simulating ? 'Simulating...' : 'Run Simulation'}
                    </Button>
                </Box>
            </Box>

            {/* Site Summary Bar (read-only) */}
            <SiteSummaryBar projectData={projectData} />

            {/* Fleet Tab (index 0) */}
            {activeTab === 0 && (
                <Box>
                    <CopyFromSelect
                        label="Copy fleet from..."
                        scenarios={allScenarios}
                        currentScenarioId={scenario.id}
                        onCopy={handleCopyFleet}
                    />
                    <FleetSection
                        fleet={scenario.fleet || []}
                        onChange={handleFleetChange}
                        projectId={projectData?.id}
                    />
                </Box>
            )}

            {/* Chargers Tab (index 1) */}
            {activeTab === 1 && (
                <Box>
                    <CopyFromSelect
                        label="Copy chargers from..."
                        scenarios={allScenarios}
                        currentScenarioId={scenario.id}
                        onCopy={handleCopyChargers}
                    />

                    {/* Demand Limit (optional site constraint) */}
                    <SectionCard title="Site Demand Limit" icon={<DemandIcon />} sx={{ mb: 3 }}>
                        <TextField
                            label="Max Demand"
                            type="number"
                            value={scenario.max_demand_kw ?? ''}
                            onChange={handleDemandLimitChange}
                            fullWidth
                            size="small"
                            placeholder={siteCapacity ? `Site capacity: ${siteCapacity} kW` : 'Leave blank for unconstrained'}
                            helperText="Optional — caps total site demand for the demand-limited strategy"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                            }}
                            inputProps={{ min: 0, max: 100000 }}
                        />
                    </SectionCard>

                    {/* Charger Configuration */}
                    <ChargerConfig
                        chargers={scenario.chargers || []}
                        chargerDatabase={chargerDatabase}
                        siteCapacity={siteCapacity}
                        onChange={handleChargersChange}
                    />
                </Box>
            )}

            {/* Utility Tab (index 2) */}
            {activeTab === 2 && (
                <UtilitySection
                    scenario={scenario}
                    projectData={projectData}
                    onChange={onChange}
                />
            )}

            {/* Schedule Tab (index 3) - Vehicle depot availability timeline */}
            {activeTab === 3 && (
                <DepotScheduleTimeline fleet={scenario.fleet || []} />
            )}

            {/* Results Tab (index 4) */}
            {activeTab === 4 && (
                <Box>
                    {scenario.results ? (
                        <>
                            <ResultsPreview results={scenario.results} strategyKey={activeStrategyKey} />

                            {/* Annual Cost Breakdown */}
                            {(() => {
                                const m = (activeStrategyKey ? scenario.results?.strategies?.[activeStrategyKey]?.metrics : null)
                                    || scenario.results?.strategies?.opportunity?.metrics
                                    || scenario.results?.metrics || {};
                                const annualEnergy = m.annual_energy_cost ?? m.energy_cost_annual ?? 0;
                                const annualDemand = m.annual_demand_cost ?? m.demand_cost_annual ?? 0;
                                const annualTotal = m.annual_total_cost ?? m.annual_cost ?? 0;
                                if (annualTotal <= 0) return null;
                                return (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            mt: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            Annual Cost Estimate
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Energy Cost
                                                </Typography>
                                                <Typography variant="h6">
                                                    ${Math.round(annualEnergy).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Demand Charges
                                                </Typography>
                                                <Typography variant="h6">
                                                    ${Math.round(annualDemand).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Total Annual
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 700 }}>
                                                    ${Math.round(annualTotal).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })()}

                            {/* Strategy selector + chart type toggle */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <ToggleButtonGroup
                                    value={activeStrategyKey}
                                    exclusive
                                    onChange={(_, v) => v && setViewStrategy(v)}
                                    size="small"
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            '&.Mui-selected': {
                                                bgcolor: alpha(vortxColors.gold, 0.15),
                                                color: vortxColors.gold,
                                                borderColor: vortxColors.gold,
                                            },
                                        },
                                    }}
                                >
                                    {['opportunity', 'demand_limit', 'cost_optimized']
                                        .filter(key => scenario.results?.strategies?.[key]?.metrics)
                                        .map(key => (
                                            <ToggleButton key={key} value={key}>
                                                {STRATEGY_LABELS[key]}
                                            </ToggleButton>
                                        ))}
                                </ToggleButtonGroup>
                                <ToggleButtonGroup
                                    value={resultsTab}
                                    exclusive
                                    onChange={(_, v) => v && setResultsTab(v)}
                                    size="small"
                                >
                                    <ToggleButton value="load">Load Profile</ToggleButton>
                                    <ToggleButton value="schedule">Charging Schedule</ToggleButton>
                                    <ToggleButton value="soc">SOC Trajectories</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Load Profile Chart */}
                            {resultsTab === 'load' && (() => {
                                const activeStrategy = (activeStrategyKey && scenario.results?.strategies?.[activeStrategyKey])
                                    || scenario.results?.strategies?.cost_optimized
                                    || scenario.results?.strategies?.demand_limit
                                    || scenario.results?.strategies?.opportunity;
                                const loads = activeStrategy?.daily_loads_hourly
                                    || activeStrategy?.loads_kw
                                    || [];
                                return (
                                    <EVLoadChart
                                        loads={loads}
                                        peakDemand={activeStrategy?.metrics?.peak_demand_kw}
                                        siteCapacity={siteCapacity}
                                        height={350}
                                    />
                                );
                            })()}

                            {/* Charging Schedule Gantt */}
                            {resultsTab === 'schedule' && (() => {
                                const activeStrategy = (activeStrategyKey && scenario.results?.strategies?.[activeStrategyKey])
                                    || scenario.results?.strategies?.cost_optimized
                                    || scenario.results?.strategies?.demand_limit
                                    || scenario.results?.strategies?.opportunity;
                                return (
                                    <ChargingScheduleChart
                                        vehicleSessions={activeStrategy?.metrics?.vehicle_sessions || []}
                                        repDaySoc={activeStrategy?.rep_day_soc || null}
                                        chargers={scenario.chargers || []}
                                        fleet={scenario.fleet || []}
                                    />
                                );
                            })()}

                            {/* SOC Trajectory Chart */}
                            {resultsTab === 'soc' && (() => {
                                // Prefer live results (active strategy), fall back to cached ref
                                const activeStrat = activeStrategyKey && scenario.results?.strategies?.[activeStrategyKey];
                                const soc = activeStrat?.soc_trajectories || socTrajectories || {};
                                return (
                                    <SOCTrajectoryChart
                                        socTrajectories={soc}
                                        targetSoc={95}
                                        minSoc={20}
                                        fleet={scenario.fleet || []}
                                        height={350}
                                    />
                                );
                            })()}
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ResultsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary" gutterBottom>
                                No simulation results yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Configure your fleet and chargers, then run a simulation.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={simulating ? <CircularProgress size={18} color="inherit" /> : <SimulateIcon />}
                                onClick={onSimulate}
                                disabled={simulating}
                                sx={{
                                    bgcolor: vortxColors.teal,
                                    '&:hover': { bgcolor: vortxColors.darkGreen },
                                }}
                            >
                                {simulating ? 'Simulating...' : 'Run Simulation'}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Financial Tab (index 5) */}
            {activeTab === 5 && (
                <FinancialSection
                    scenario={scenario}
                    projectData={projectData}
                    onChange={onChange}
                />
            )}
        </Box>
    );
}

export default ScenarioEditor;
